'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

type YTPlayer = {
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  setPlaybackRate: (rate: number) => void;
  getPlaybackRate: () => number;
  mute: () => void;
  unMute: () => void;
  isMuted: () => boolean;
  setVolume: (volume: number) => void;
  getVolume: () => number;
  destroy: () => void;
};

declare global {
  interface Window {
    YT?: {
      Player: new (
        element: HTMLElement | string,
        options: Record<string, unknown>
      ) => YTPlayer;
      PlayerState: { PLAYING: number; PAUSED: number; ENDED: number };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

function loadYouTubeApi(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.YT?.Player) return Promise.resolve();

  return new Promise((resolve) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      resolve();
    };

    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(script);
    }

    if (window.YT?.Player) resolve();
  });
}

function formatTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface YouTubePlayerModalProps {
  videoId: string;
  title: string;
  onClose: () => void;
}

export default function YouTubePlayerModal({ videoId, title, onClose }: YouTubePlayerModalProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const draggingRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(80);
  const [showSpeeds, setShowSpeeds] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let player: YTPlayer | null = null;

    loadYouTubeApi().then(() => {
      const tryCreate = () => {
        if (cancelled || !window.YT?.Player) return;
        if (!hostRef.current) {
          requestAnimationFrame(tryCreate);
          return;
        }

        player = new window.YT.Player(hostRef.current, {
          videoId,
          width: '100%',
          height: '100%',
          host: 'https://www.youtube-nocookie.com',
          playerVars: {
            autoplay: 1,
            controls: 0,
            disablekb: 1,
            fs: 0,
            modestbranding: 1,
            rel: 0,
            iv_load_policy: 3,
            playsinline: 1,
            origin: window.location.origin,
          },
          events: {
            onReady: (event: { target: YTPlayer }) => {
              if (cancelled) return;
              event.target.setVolume(80);
              setDuration(event.target.getDuration() || 0);
              setReady(true);
              event.target.playVideo();
            },
            onStateChange: (event: { data: number }) => {
              const playingState = window.YT?.PlayerState.PLAYING;
              setPlaying(event.data === playingState);
              const dur = playerRef.current?.getDuration();
              if (dur) setDuration(dur);
            },
          },
        });
        playerRef.current = player;
      };

      tryCreate();
    });

    return () => {
      cancelled = true;
      playerRef.current?.destroy();
      playerRef.current = null;
    };
  }, [videoId]);

  useEffect(() => {
    if (!ready) return;
    const id = window.setInterval(() => {
      if (!playerRef.current || draggingRef.current) return;
      setCurrent(playerRef.current.getCurrentTime() || 0);
    }, 250);
    return () => window.clearInterval(id);
  }, [ready]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === ' ' ) {
        e.preventDefault();
        const player = playerRef.current;
        if (!player) return;
        const state = player.getPlayerState();
        if (state === window.YT?.PlayerState.PLAYING) player.pauseVideo();
        else player.playVideo();
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const togglePlay = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    const state = player.getPlayerState();
    if (state === window.YT?.PlayerState.PLAYING) player.pauseVideo();
    else player.playVideo();
  }, []);

  const seek = (value: number) => {
    playerRef.current?.seekTo(value, true);
    setCurrent(value);
  };

  const changeSpeed = (rate: number) => {
    playerRef.current?.setPlaybackRate(rate);
    setSpeed(rate);
    setShowSpeeds(false);
  };

  const toggleMute = () => {
    const player = playerRef.current;
    if (!player) return;
    if (player.isMuted()) {
      player.unMute();
      setMuted(false);
    } else {
      player.mute();
      setMuted(true);
    }
  };

  const changeVolume = (value: number) => {
    playerRef.current?.setVolume(value);
    setVolume(value);
    if (value === 0) {
      playerRef.current?.mute();
      setMuted(true);
    } else if (muted) {
      playerRef.current?.unMute();
      setMuted(false);
    }
  };

  const progress = duration > 0 ? (current / duration) * 100 : 0;

  return createPortal(
    <div className="fixed inset-0 z-[100]">
      <button
        type="button"
        className="absolute inset-0 bg-black/90 backdrop-blur-sm cursor-pointer"
        aria-label="إغلاق"
        onClick={onClose}
      />

      <div className="relative z-[101] pointer-events-none flex min-h-full items-center justify-center px-5 py-16 sm:p-10 lg:p-16">
      <div
        className="pointer-events-auto relative w-full max-w-5xl"
        dir="ltr"
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute z-[110] -top-3 -right-3 sm:-top-4 sm:-right-4 w-12 h-12 sm:w-11 sm:h-11 rounded-full bg-amber-500 text-black shadow-[0_8px_24px_rgba(0,0,0,0.45)] flex items-center justify-center hover:bg-amber-400 active:scale-95 transition-all"
          aria-label="إغلاق"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div
          className="overflow-hidden rounded-2xl border border-amber-900/30"
          style={{ background: '#0f0f0f' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-5 py-3 pr-14 border-b border-amber-900/20" dir="rtl">
            <h3 className="text-white font-bold text-base sm:text-lg">{title}</h3>
          </div>

          <div
            className="relative bg-black aspect-video [&_iframe]:absolute [&_iframe]:inset-0 [&_iframe]:h-full [&_iframe]:w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div ref={hostRef} className="absolute inset-0 w-full h-full" />
            <button
              type="button"
              className="absolute inset-0 z-10 cursor-pointer"
              aria-label={playing ? 'إيقاف مؤقت' : 'تشغيل'}
              onClick={togglePlay}
            />
            {!ready && (
              <div className="absolute inset-0 z-20 flex items-center justify-center text-amber-400 text-sm">
                جاري التحميل...
              </div>
            )}
          </div>

          <div className="p-3 sm:p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-3">
            <span className="text-gray-400 text-xs tabular-nums w-10">{formatTime(current)}</span>
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={current}
              onMouseDown={() => {
                draggingRef.current = true;
              }}
              onMouseUp={(e) => {
                draggingRef.current = false;
                seek(Number((e.target as HTMLInputElement).value));
              }}
              onTouchStart={() => {
                draggingRef.current = true;
              }}
              onTouchEnd={(e) => {
                draggingRef.current = false;
                const input = e.currentTarget;
                seek(Number(input.value));
              }}
              onChange={(e) => setCurrent(Number(e.target.value))}
              className="video-progress flex-1 h-1.5 accent-amber-500 cursor-pointer"
              style={{
                background: `linear-gradient(to right, #d97706 ${progress}%, #27272a ${progress}%)`,
              }}
              aria-label="شريط التقدم"
            />
            <span className="text-gray-400 text-xs tabular-nums w-10 text-right">{formatTime(duration)}</span>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={togglePlay}
                className="w-10 h-10 rounded-xl bg-amber-600 text-black flex items-center justify-center hover:bg-amber-500 transition-colors"
                aria-label={playing ? 'إيقاف مؤقت' : 'تشغيل'}
              >
                {playing ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 5h4v14H6V5zm8 0h4v14h-4V5z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7L8 5z" />
                  </svg>
                )}
              </button>

              <button
                type="button"
                onClick={toggleMute}
                className="w-10 h-10 rounded-xl bg-amber-900/20 text-amber-400 border border-amber-900/30 flex items-center justify-center hover:bg-amber-900/40 transition-colors"
                aria-label={muted ? 'إلغاء كتم الصوت' : 'كتم الصوت'}
              >
                {muted || volume === 0 ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15zM17 14l4-4m0 4l-4-4" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  </svg>
                )}
              </button>
              <input
                type="range"
                min={0}
                max={100}
                value={muted ? 0 : volume}
                onChange={(e) => changeVolume(Number(e.target.value))}
                className="w-24 h-1.5 accent-amber-500 cursor-pointer"
                aria-label="مستوى الصوت"
              />
            </div>

            <div className="flex items-center gap-1 overflow-x-auto max-w-[55%] justify-end">
              {showSpeeds && SPEEDS.map((rate) => (
                <button
                  key={rate}
                  type="button"
                  onClick={() => changeSpeed(rate)}
                  className={`shrink-0 px-2.5 py-1.5 rounded-lg text-xs sm:text-sm ${
                    speed === rate
                      ? 'bg-amber-500 text-black font-bold'
                      : 'text-gray-300 bg-amber-900/20 hover:bg-amber-900/40'
                  }`}
                >
                  {rate}x
                </button>
              ))}
              <button
                type="button"
                onClick={() => setShowSpeeds((open) => !open)}
                className="shrink-0 px-3 py-2 rounded-xl bg-amber-900/20 text-amber-400 border border-amber-900/30 text-sm font-bold hover:bg-amber-900/40 transition-colors"
              >
                {speed}x
              </button>
            </div>
          </div>
          </div>
        </div>
      </div>
      </div>
    </div>,
    document.body
  );
}
